import type { KycFileCertificationChoiceDto } from '@dealstack/shared/lib/generated';
import { uploadFileV2 } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import {
	addProofOfPIV,
	deleteProofOfPIV,
	getProofsOfPIV,
	resetInheritedKycItems,
} from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { KycResetScope } from '@dealstack/shared/lib/common/business/kyc';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useFileUploadErrorHandler } from '@dealstack/frontend/investor/hooks/useFileUploadErrorHandler';
import { useKycFileConfirmation } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/useKycFileConfirmation';
import { hasUnconfirmedKycFiles } from '@dealstack/frontend/investor/pages/Investment/common/checksComplete/utils.ts';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	nothingToSave,
	useWizardStep,
} from '@dealstack/frontend/shared/components/Wizard/WizardStepRegistry';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { GetInvestmentDetailsRes } from '@dealstack/shared/lib/generated';

export enum PIV_PROOFS_FORM_FIELD {
	SHARE_REGISTER = 'SHARE_REGISTER',
	CERTIFICATE_OF_REGISTRATION = 'CERTIFICATE_OF_REGISTRATION',
	ARTICLES_OF_ASSOCIATION = 'ARTICLES_OF_ASSOCIATION',
	SIGNATORY_POWER = 'SIGNATORY_POWER',
}

export interface PivProofValues {
	[PIV_PROOFS_FORM_FIELD.SHARE_REGISTER]: boolean;
	[PIV_PROOFS_FORM_FIELD.CERTIFICATE_OF_REGISTRATION]: boolean;
	[PIV_PROOFS_FORM_FIELD.ARTICLES_OF_ASSOCIATION]: boolean;
	[PIV_PROOFS_FORM_FIELD.SIGNATORY_POWER]: boolean;
}

interface Props {
	setIsContinueDisabled: (isContinueDisabled: boolean) => void;
	investmentDetails: GetInvestmentDetailsRes | undefined;
}

export const useInvestmentLeavePIVProofController = ({
	setIsContinueDisabled,
	investmentDetails,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const handleUploadError = useFileUploadErrorHandler();
	const { createConfirmHandler } = useKycFileConfirmation();

	const kycWorkflowId = investmentDetails?.activeKycWorkflowId ?? '';

	const pivProofsQuery = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_KYC_INVESTMENT_VEHICLE_GET, kycWorkflowId],
		queryFn: () => getProofsOfPIV(kycWorkflowId),
		enabled: !!kycWorkflowId,
	});

	const uploadFileMutation = useMutation({
		mutationFn: uploadFileV2,
		onError: handleUploadError,
	});
	const addProofMutation = useMutation({
		mutationFn: (variables: Parameters<typeof addProofOfPIV>[0]) => addProofOfPIV(variables),
	});
	const deleteProofMutation = useMutation({
		mutationFn: (variables: Parameters<typeof deleteProofOfPIV>[0]) =>
			deleteProofOfPIV(variables),
	});

	const onFileDrop = (file: File, certification?: KycFileCertificationChoiceDto) => {
		uploadFileMutation.mutate(file, {
			onSuccess: ({ payload }) => {
				if (!payload) {
					return;
				}
				addProofMutation.mutate(
					{ fileId: payload.id, kycWorkflowId, certification },
					{
						onSuccess: () => {
							showToastV2({ title: tInvestor('investment.toast.proof_uploaded') });
							void pivProofsQuery.refetch();
						},
					}
				);
			},
		});
	};

	const proofFilesDetails = pivProofsQuery.data?.payload;

	const onConfirm = (proofId: string) =>
		createConfirmHandler(proofId, () => void pivProofsQuery.refetch());

	const onResetAll = async () => {
		await resetInheritedKycItems(kycWorkflowId, KycResetScope.VEHICLE);
		void pivProofsQuery.refetch();
	};

	const onDelete = (proofId: string) => () => {
		const proofToDelete = proofFilesDetails?.find((proof) => proof.id === proofId);
		if (!proofToDelete) {
			return;
		}

		deleteProofMutation.mutate(
			{ fileId: proofToDelete.id, kycWorkflowId },
			{
				onSuccess: () => {
					showToastV2({ title: tInvestor('investment.toast.proof_removed') });
					void pivProofsQuery.refetch();
				},
			}
		);
	};

	const pivProofsForm = useForm<PivProofValues>();

	useEffect(() => {
		const hasUnconfirmed = hasUnconfirmedKycFiles(proofFilesDetails ?? []);
		const isAnyRejected = !!proofFilesDetails?.some(
			({ status }) => status === 'REJECTED' || status === 'REREQUESTED'
		);
		setIsContinueDisabled(!proofFilesDetails?.length || isAnyRejected || hasUnconfirmed);
	}, [proofFilesDetails]);

	useWizardStep(nothingToSave);

	return {
		refetchProofs: () => void pivProofsQuery.refetch(),
		uploadFileRequest: uploadFileMutation,
		proofFilesDetails,
		onDelete,
		onConfirm,
		onResetAll,
		pivProofsForm,
		onFileDrop,
	};
};
